import React, { useState, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import { ThemeProvider, BaseStyles, FormControl, Select, Checkbox, CheckboxGroup } from "@primer/react";
import { DownloadIcon } from "@primer/octicons-react";
import { handleEventClick } from "./calendarTooltip";
import { StatusCheckboxes, statusValues } from "./facilityFilters";
import {
  weekStartOf,
  isoDate,
  buildEntryChip,
  dayTotalLabel,
  weekSummaryText,
  downloadHref,
} from "./facilityScheduleContent";

// GR-3219 — the operational read-out of the Facilities Matrix. Same resource
// timeline, locked to day slots, with the payload a catering manager works
// from rendered into the chip instead of hidden behind a hover.
//
// No Venue entry: the server's FACILITY_TYPES leaves venues to the matrix,
// which already schedules rooms on a timeline, so the type switch never
// offers them.
const TYPE_LABELS = {
  Consumable: "Catering",
  Equipment: "Equipment",
  Service: "Services",
};

// Two Excel layouts, deliberately both offered: the long format is one flat
// row per facility x day x booking (paste it into a system), the caterer sheet
// is the week broken into day sections with the kitchen's own columns and the
// blanks they fill in after downloading.
const DOWNLOAD_FORMATS = [
  { format: "csv", label: "CSV" },
  { format: "xlsx", label: "Excel" },
  { format: "xlsx", layout: "caterer", label: "Caterer sheet" },
];

function ScheduleFilters({ filters, onChange, facilityTypes, downloadUrl, weekSummary, requestStatuses }) {
  return (
    <ThemeProvider>
      <BaseStyles style={{ background: "transparent" }}>
        <div className="d-flex flex-column" style={{ gap: "16px" }}>
          {facilityTypes.length > 1 && (
            <FormControl>
              <FormControl.Label>Facility Type</FormControl.Label>
              <Select
                value={filters.facilityType}
                onChange={(e) => onChange((prev) => ({ ...prev, facilityType: e.target.value }))}
                block
                size="large"
              >
                {facilityTypes.map((type) => (
                  <Select.Option key={type} value={type}>
                    {TYPE_LABELS[type] || type}
                  </Select.Option>
                ))}
              </Select>
            </FormControl>
          )}

          <StatusCheckboxes
            statuses={requestStatuses}
            selected={filters.statuses}
            onChange={(statuses) => onChange((prev) => ({ ...prev, statuses }))}
          />

          <CheckboxGroup>
            <CheckboxGroup.Label>Display</CheckboxGroup.Label>
            <FormControl>
              <Checkbox
                checked={filters.weekends}
                onChange={(e) => onChange((prev) => ({ ...prev, weekends: e.target.checked }))}
              />
              <FormControl.Label>Show Weekends</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.includeArchived}
                onChange={(e) => onChange((prev) => ({ ...prev, includeArchived: e.target.checked }))}
              />
              <FormControl.Label>Show Archived / Cancelled</FormControl.Label>
              <FormControl.Caption>Shown on the timeline only: not in the day totals or the downloads.</FormControl.Caption>
            </FormControl>
          </CheckboxGroup>

          <div>
            <div className="text-bold f6 mb-2">Download this week</div>
            <div className="d-flex" style={{ gap: "8px" }}>
              {DOWNLOAD_FORMATS.map(({ format, layout, label }) => (
                <a
                  key={layout ? `${format}-${layout}` : format}
                  className="btn btn-sm"
                  href={downloadUrl(format, layout)}
                  data-turbo="false"
                  data-test-download={layout ? `${format}-${layout}` : format}
                >
                  <DownloadIcon size={14} /> <span className="ml-1">{label}</span>
                </a>
              ))}
            </div>
          </div>

          {weekSummary && <div className="f6 color-fg-muted">{weekSummaryText(weekSummary)}</div>}
        </div>
      </BaseStyles>
    </ThemeProvider>
  );
}

export function FacilityDaySchedule({
  eventsUrl,
  schedulePath,
  timezone,
  facilities = [],
  facilityTypes = [],
  initialFacilityType,
  initialDate,
  firstDay = 1,
  requestStatuses = [],
}) {
  const calendarRef = useRef(null);
  const [filters, setFilters] = useState({
    facilityType: initialFacilityType || facilityTypes[0] || "",
    weekends: true,
    includeArchived: false,
    statuses: statusValues(requestStatuses),
  });
  const [dayTotals, setDayTotals] = useState({});
  const [weekSummary, setWeekSummary] = useState(null);
  const [weekStart, setWeekStart] = useState(initialDate || null);

  const resources = useMemo(
    () =>
      facilities
        .filter((f) => !filters.facilityType || f.type === filters.facilityType)
        // GR-3081: sortIndex preserves the server's manual ordering, which
        // FullCalendar would otherwise replace with an id sort.
        .map((f, index) => ({
          id: String(f.id),
          title: f.name,
          sortIndex: index,
          extendedProps: { type: f.type },
        })),
    [facilities, filters.facilityType]
  );

  const fetchEvents = useCallback(
    (fetchInfo, successCallback, failureCallback) => {
      const url = new URL(eventsUrl, window.location.origin);
      url.searchParams.set("start", fetchInfo.startStr);
      url.searchParams.set("end", fetchInfo.endStr);
      if (filters.facilityType) url.searchParams.set("facility_type", filters.facilityType);
      // Unlike the matrix, the status filter goes to the server rather than
      // being applied to the response here: this view reports day totals, and
      // filtering the chips locally would leave a day header claiming covers
      // over a column it had just emptied.
      filters.statuses.forEach((status) => url.searchParams.append("statuses[]", status));
      // GR-4399: Show Archived / Cancelled, the same param as the matrix.
      if (filters.includeArchived) url.searchParams.set("include_archived", "true");

      fetch(url, { headers: { Accept: "application/json" } })
        .then((res) => res.json())
        .then((data) => {
          setDayTotals(data.dayTotals || {});
          setWeekSummary(data.weekSummary || null);
          successCallback(data.events);
        })
        .catch(failureCallback);
    },
    [eventsUrl, filters]
  );

  const eventContent = useCallback(
    (arg) => ({ domNodes: [buildEntryChip(arg.event.title, arg.event.extendedProps)] }),
    []
  );

  // Day totals belong on the day header, where they read as "what Wednesday
  // needs" rather than as a footer row the timeline has no place for.
  const slotLabelContent = useCallback(
    (arg) => {
      const wrap = document.createElement("div");
      wrap.className = "fc-schedule-slot";

      const label = document.createElement("div");
      label.textContent = arg.text;
      wrap.appendChild(label);

      const total = dayTotalLabel(dayTotals[isoDate(arg.date)]);
      if (total) {
        const node = document.createElement("div");
        node.className = "fc-schedule-slot__total";
        node.textContent = total;
        wrap.appendChild(node);
      }

      return { domNodes: [wrap] };
    },
    [dayTotals]
  );

  const handleDatesSet = useCallback((arg) => setWeekStart(weekStartOf(arg.start)), []);

  const downloadUrl = useCallback(
    (format, layout) =>
      downloadHref({
        schedulePath,
        facilityType: filters.facilityType,
        weekStart,
        format,
        layout,
        statuses: filters.statuses,
      }),
    [schedulePath, filters.facilityType, filters.statuses, weekStart]
  );

  const filtersPortal = document.getElementById("schedule-filters-portal");

  return (
    <div className="admin-events-calendar admin-facility-day-schedule">
      {filtersPortal &&
        createPortal(
          <ScheduleFilters
            filters={filters}
            onChange={setFilters}
            facilityTypes={facilityTypes}
            downloadUrl={downloadUrl}
            weekSummary={weekSummary}
            requestStatuses={requestStatuses}
          />,
          filtersPortal
        )}
      <FullCalendar
        ref={calendarRef}
        plugins={[resourceTimelinePlugin]}
        initialView="resourceTimelineWeek"
        initialDate={initialDate || undefined}
        firstDay={firstDay}
        headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
        buttonText={{ today: "This week" }}
        resources={resources}
        events={fetchEvents}
        eventContent={eventContent}
        eventClick={handleEventClick}
        datesSet={handleDatesSet}
        timeZone={timezone || "local"}
        height="auto"
        resourceOrder="sortIndex"
        resourceAreaHeaderContent="Facility"
        // Wider than the matrix's 180px: these lanes are tall enough that a
        // truncated facility name is the only thing anchoring the row.
        resourceAreaWidth="220px"
        // Day slots are the point of this view, so unlike the matrix there is
        // no zoom control to take it off the day grain.
        slotDuration={{ days: 1 }}
        slotLabelFormat={[{ weekday: "short", month: "numeric", day: "numeric" }]}
        slotLabelContent={slotLabelContent}
        slotMinTime="00:00:00"
        slotMaxTime="24:00:00"
        weekends={filters.weekends}
        nowIndicator
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
      />
    </div>
  );
}
